import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import ProductCategoriesApiV2 from 'api/v2/admins/product-categories-api-v2'
import LoaderButton from 'components/molecules/loader-button'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { CODE } from 'domains/product-attribute/constants'
import ProductAttributeService from 'domains/product-attribute/services'
import ProductCategorySelectField from 'domains/product-category/molecules/select-field'
import SellerInboundSourceInputSelectField from 'domains/seller-inbound/molecules/inbound-source-input-select-field'
import SellerProductService from 'domains/seller-product/services/seller-product'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import CurateAcceptProductAttributeFields from './curate-accept-product-attribute-fields'
import defaultSchema from './seller-inbound-curate-accept-product-schema'
import GenderFilterField from 'domains/product/organisms/gender-filter-field'


const propTypes = {
  handleSubmit  : PropTypes.func,
  seller_type   : PropTypes.string,
  sellerProduct : PropTypes.object
}

// * In Seller Curation form, we don't want to input quantity
const EXCLUDED_ATTRIBUTE_KEYS = [
  CODE.QUANTITY,
  CODE.CATEGORY
]

const DEFAULT_MODAL_MESSAGE = 'Are you sure want to accept the product?'

// TODO: add size to Product Attribute in the database. so we don't need to hardcoded size initial value here
const defaultInitialValues = {
  gender         : '',
  inbound_source : '',
  category       : '',
  size           : ''
}

/**
 * Curate accept product for Seller Product.
 * @param {func} handleSubmit - Handler to submit curation accept form
 * @param {string} seller_type - Seller's type
 */
const SellerInboundCurateAcceptProductForm = ({
  sellerProduct,
  handleSubmit,
  seller_type
}) => {
  const [ reinitializeForm, setReinitializeForm ] = useState(false)
  const [ attributes, setAttributes ]             = useState([])
  const [ sizes, setSizes ]                       = useState([])
  const [ gender, setGender ]                     = useState()

  const [ initalValues, setInitialValues ]                      = useState(defaultInitialValues)
  const [ dynamicValidationSchema, setDynamicValidationSchema ] = useState(defaultSchema(sizes))
  const [ isModalOpen, setIsModalOpen ]                         = useState(false)
  const [ modalMessage, setModalMessage ]                       = useState(DEFAULT_MODAL_MESSAGE)


  /** Toggle for Confirmation modal */
  const toggleAcceptModal = () => setIsModalOpen((prev) => !prev)

  /**
   * Generate message for the confirmation modal
   * @param {object} value - Formik's values
   */
  const generateMessageModal = (values) => {
    const { price } = values

    price < 50000
      ? setModalMessage(`The price of the product is less than Rp. 50,000. ${DEFAULT_MODAL_MESSAGE}`)
      : setModalMessage(DEFAULT_MODAL_MESSAGE)
  }

  // * using useCallback to prevent the component always re-rendering
  const onFetchAttributes = (category_id) => {
    ProductAttributesApiV2.getCurationAttributes(category_id)
      .then((response) => {
        // * exclude particular attributes
        const filteredAttributes = response.data.attributes.filter((attribute) => !EXCLUDED_ATTRIBUTE_KEYS.includes(attribute.code))

        setAttributes(filteredAttributes)
        setReinitializeForm(true)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  // * using useCallback to prevent the component always re-rendering
  const onFetchSizes = (category_id) => {
    ProductCategoriesApiV2.getSizes(
      category_id,
      gender
    )
      .then((response) => {
        setSizes(response.data.sizes)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  /**
   * Handle logics everytime category has changed
   * - refetching sizes depend on `category_id`
   * - refetching attributes depend `on category_id`
   * - overwrite `category_id` in `initialValues`
   */
  const handleChangeCategory = useCallback((category_id) => {
    onFetchSizes(category_id)
    onFetchAttributes(category_id)

    // * Change category with current value
    // * We still keep the other values
    setInitialValues((prev) => ({
      ...prev,
      gender,
      category: category_id
    }))
  }, [ gender ])

  useEffect(() => {
    // * Set initial values of the form
    // * If seller product props exists, it will set the value to the fields (for Edit Curation case)
    const currentValues = sellerProduct
      ? SellerProductService.getAcceptedProductAttributeValues(sellerProduct)
      : defaultInitialValues

    setInitialValues(currentValues)
  }, [ sellerProduct ])

  useEffect(() => {

    if (reinitializeForm) {
      // * Two things happen when reinitialized the form
      // * - generate new validation schema
      // * - build new initial values. It is needed in order to trigger validation

      const attributesSchema          = ProductAttributeService.buildFormikValidationSchema(attributes)
      const generatedValidationSchema = defaultSchema(sizes).concat(attributesSchema)

      const newInitialValues = ProductAttributeService.buildFormikInitialValue(
        attributes,
        // * to add Size default value
        { size: '', ...initalValues }
      )

      setDynamicValidationSchema(generatedValidationSchema)
      setInitialValues(newInitialValues)

      // * to make sure React stop to re-render
      setReinitializeForm(false)
    }

  }, [ reinitializeForm, sizes, attributes, initalValues ])

  const handleGenderChange = (event, formikBag) => {
    formikBag.setFieldValue('gender', event.target.value)
    formikBag.setFieldValue('category', '')
    setGender(event.target.value)
    setReinitializeForm(true)
  }

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={ initalValues }
      validationSchema={ dynamicValidationSchema }
      onSubmit={ (value, formikBag) => {
        formikBag.setSubmitting(false)
        generateMessageModal(value)
        toggleAcceptModal()
      } }
    >
      {
        (formikBag) => (
          <Form>
            <GenderFilterField
              required={ true }
              onChange={ (event) => handleGenderChange(event, formikBag) }
            />
            <ProductCategorySelectField
              name='category'
              label='Category'
              required={ true }
              gender={ formikBag.values.gender }
            />

            <SellerInboundSourceInputSelectField
              name='inbound_source'
              label='Inbound Source'
              required={ true }
              seller_type={ seller_type }
            />

            <CurateAcceptProductAttributeFields
              attributes={ attributes }
              sizes={ sizes }
              onChangeCategory={ handleChangeCategory }
            />

            <LoaderButton
              color='primary'
              type='submit'
              onSubmit={ formikBag.submitForm }
              disabled={ formikBag.isSubmitting }
              isLoading={ formikBag.isSubmitting }
              text='Curate'
            />

            <ConfirmationModal
              isOpen={ isModalOpen }
              toggle={ toggleAcceptModal }
              title={ 'Accept the Product' }
              handleConfirmation={ () => {
                handleSubmit(formikBag.values, attributes, formikBag)
                toggleAcceptModal()
              } }
            >
              { modalMessage }
            </ConfirmationModal>

          </Form>
        )
      }
    </Formik>
  )
}

SellerInboundCurateAcceptProductForm.propTypes = propTypes

export default SellerInboundCurateAcceptProductForm
